import {forwardRef, Inject, Injectable} from '@angular/core';
import {HttpAuthService} from '@shared/service/http-auth.service';
import {Utilities} from '@shared/utilities/utilities.model';
import {Observable} from 'rxjs/Observable';
import {UserV3Model} from '@shared/models/user-v-3.model';
import {UsersListModel} from '@shared/models/users-list.model';
import {UsersFilterModel} from '@shared/models/users-filter.model';
import {ListService} from '@shared/models/list-service.model';
import {Subject} from 'rxjs/Subject';
import {AppConfigEnvironmentService} from '@shared/service/appconfig-environment.service';
import {AppConfigEnvironment} from '@shared/models/appconfig-environment.model';
import {TagItemSettings} from '../models/tag-item.model';

@Injectable()
export class UserV3Service implements ListService {

	private userSubject = new Subject<UserV3Model>();
	private userObservable = this.userSubject.asObservable();
	private user: UserV3Model;

	private userGetting = false;

	private alertLastAccessSubject = new Subject<string>();
	alertLastAccessObservable = this.alertLastAccessSubject.asObservable();

	environment: AppConfigEnvironment;
	NOT_FOUND = 'NOT_FOUND';
	NOT_VALID = 'NOT_VALID';
	DUPLICATE_VALUE = 'DUPLICATE_VALUE';
	TECHNICAL_ERROR = 'TECHNICAL_ERROR';

	constructor(@Inject(forwardRef(() => HttpAuthService)) public httpAuthService: HttpAuthService,
		private appConfig: AppConfigEnvironmentService) {
		this.environment = appConfig.Environment;

	}

	getUserById(user_id: string) {
		const url: string = this.environment.USER_API_V3 + '/users/' + user_id;
		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH());
	}

	getCurrentUser(): UserV3Model {
		return this.user;
	}

	getUser(): Observable<UserV3Model> {
		if (!this.userGetting) {
			this.userGetting = true;
			this.requestUser()
		}
		if (Utilities.isVoid(this.user)) {
			return this.userObservable
		}
		return new Observable<UserV3Model>(observer => observer.next(this.user));
	}

	private requestUser() {
		const url: string = this.environment.USER_API_V3 + '/users/current';
		this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH(), true).subscribe(
			response => {
				if (response !== null && response.status === 200) {
					const model = response.json();
					if (!Utilities.isVoid(model)) {
						this.user = new UserV3Model(model);
						this.userSubject.next(this.user);
					} else {
						this.userSubject.error('model: ' + model);
					}
				} else {
					this.userSubject.error('response: ' + response);
				}
			},
			error => {
				console.error(error);
				this.userSubject.error(error);
			});
	}

	getProfileData(): Observable<UserV3Model> {
		const url: string = this.environment.USER_API_V3 + '/users/current/profile_data';
		return new Observable<UserV3Model>(observer => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH()).subscribe(
				response => {
					if (response !== null && response.status === 200) {
						const model = response.json();
						if (!Utilities.isVoid(model)) {
							this.user.personal_details.setProfileData(model.personal_details);
							observer.next(this.user);
						} else {
							observer.error('model: ' + model);
						}
					} else {
						observer.error('response: ' + response);
					}
				},
				error => {
					console.error(error);
				});
		});
	}

	getCustomData(): Observable<UserV3Model> {
		const url: string = this.environment.USER_API_V3 + '/users/current/custom_data';
		return new Observable<UserV3Model>(observer => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH()).subscribe(
				response => {
					if (response !== null && response.status === 200) {
						const model = response.json();
						if (!Utilities.isVoid(model) && !Utilities.isVoid(model.custom_data)) {
							this.user.setCustomData(model.custom_data);
							observer.next(this.user);
						} else {
							const m = { app_settings: { accessed_pages: [] } };
							this.user.setCustomData(m);
							observer.next(this.user);
							// observer.error('model: ' + model);
						}
					} else {
						observer.error('response: ' + response);
					}
				},
				error => {
					console.error(error);
				});
		});
	}


	setCustomData(levels: string[], body: any): Observable<void> {
		let url: string = this.environment.USER_API_V3 + '/users/current/custom_data';
		for (const level of levels) {
			url += '/' + level;
		}
		return new Observable<void>(observer => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH())
				.subscribe(
					response => {
						if (!Utilities.isVoid(response) && response.ok) {
							this.getCustomData().subscribe(
								() => {
									observer.next();
								}, error => {
									observer.error(error);
								});
						} else {
							observer.error('response: ' + response);
						}
					}, error => {
						console.error(error);
					});
		});
	}

	createUser(body: any): Observable<string> {
		const url: string = this.environment.USER_API_V3 + '/users';
		return new Observable<string>(observer => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH())
				.subscribe(
					response => {
						if (response !== null && response.ok) {
							const model = response.json();
							if (!Utilities.isVoid(model) && !Utilities.isVoid(model.user_id)) {
								observer.next(model.user_id);
							} else {
								observer.error('model: ' + model);
							}
						} else {
							observer.error(response);
						}
					},
					error => {
						console.error(error);
					});
		});
	}

	updateUser(user_id: string, body: UserV3Model): Observable<void> {
		const url: string = this.environment.USER_API_V3 + '/users/' + user_id;
		return new Observable<void>(observer => {
			this.httpAuthService.put(url, body, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH())
				.subscribe(
					response => {
						if (response !== null && response.status === 204) {
							observer.next();
						} else {
							observer.error(response);
						}
					},
					error => {
						console.error(error);
					});
		});
	}

	deleteUser(user_id: string): Observable<void> {
		const url: string = this.environment.USER_API_V3 + '/users/' + user_id;
		return new Observable<void>(observer => {
			this.httpAuthService.delete(url, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH())
				.subscribe(
					response => {
						if (response !== null && response.status === 204) {
							observer.next();
						} else {
							observer.error('response: ' + response);
						}
					},
					error => {
						console.error(error);
					});
		});
	}

	getList(filter: UsersFilterModel, params?: any): Observable<UsersListModel> {
		let url: string = this.environment.USER_API_V3 + '/users';
		if (filter) {
			url = url + filter.getFilterUrl();
		}
		return new Observable<UsersListModel>(observer => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH()).subscribe(
				response => {
					if (response !== null && response.status === 200) {
						const model = response.json();
						if (!Utilities.isVoid(model)) {
							observer.next(new UsersListModel(model));
						} else {
							observer.error('model: ' + model);
						}
					} else {
						observer.error('response: ' + response);
					}
				},
				error => {
					console.error(error);
				});
		});
	}

	checkSurveyor(surveyorId: string): Observable<UserV3Model> {
		let url: string = this.environment.USER_API_V3 + '/users';
		const filter = new UsersFilterModel();
		filter.lastname = surveyorId;
		if (filter) {
			url = url + filter.getFilterUrl();
		}
		return new Observable<UserV3Model>(observer => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH()).subscribe(
				response => {
					if (response !== null && response.status === 200) {
						const model = response.json();
						if (!Utilities.isVoid(model)) {
							const list = new UsersListModel(model);
							if (!Utilities.isVoid(list)) {
								if (list.items.length > 0) {
									const surveyorList = list.items.filter(code => code.personal_details.lastname === surveyorId);
									if (surveyorList.length === 0) {
										observer.error(this.NOT_VALID);
									} else if (surveyorList.length === 1) {
										observer.next(surveyorList[0]);
									} else {
										observer.error(this.DUPLICATE_VALUE);
									}
								} else {
									observer.error(this.NOT_FOUND);
								}
							}
						} else {
							observer.error('model: ' + model);
						}
					} else {
						//observer.error('response: ' + response);
						observer.error(this.TECHNICAL_ERROR);
					}
				},
				error => {
					console.error(error);
				});
		});
	}

	checkSurveyorByFilter(filter?: UsersFilterModel): Observable<UserV3Model[]> {
		let url: string = this.environment.USER_API_V3 + '/users';
		if (!Utilities.isVoid(filter)) {
			url = url + filter.getFilterUrl();
		}
		return new Observable<UserV3Model[]>(observer => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, Utilities.setRequestOptionAUTH()).subscribe(
				response => {
					if (response !== null && response.status === 200) {
						const model = response.json();
						if (!Utilities.isVoid(model)) {
							const list = new UsersListModel(model);
							// observer.next(list.stats.itemsCount === 1)
							observer.next(list.items);
						} else {
							observer.error('model: ' + model);
						}
					} else {
						observer.error('response: ' + response);
					}
				},
				error => {
					console.error(error);
				});
		});
	}

	getAlertsLastAccess(): string {
		if (!Utilities.isVoid(this.user)) {
			return this.user.getAlertsLastAccess();
		}
		return '';
	}

	getAccessedPages(): string[] {
		if (!Utilities.isVoid(this.user)) {
			return this.user.getAccessedPages();
		}
		return [];
	}

	addAccessedPage(page: string) {
		this.user.addAccessedPage(page);
	}

	getAlertTags(): TagItemSettings[] {
		if (!Utilities.isVoid(this.user)) {
			return this.user.getAlertTags();
		}
		return [];
	}

	setAlertTags(tags: TagItemSettings[]) {
		this.user.setAlertTags(tags);
	}

	isPageAccessed(page: string): boolean {
		return !Utilities.isVoid(this.user) && this.user.isPageAccessed(page);
	}

	getTemplate(): number {
		if (!Utilities.isVoid(this.user)) {
			return this.user.getTemplate();
		}
		return 0;
	}

	getItemsPerPage(): number {
		if (!Utilities.isVoid(this.user)) {
			return this.user.getItemsPerPage();
		}
		return 10;
	}

	getNotShowAddToHome() {
		return !Utilities.isVoid(this.user) && this.user.getNotShowAddToHome();

	}

	pushNewAlertsLastAccess() {
		this.alertLastAccessSubject.next();
	}
}

